import type { FC, PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useInRouterContext, useSearchParams } from 'react-router-dom';
import { useClientStoredResourcesContext } from '@pflegenavi/frontend/client-stored-resources-context';
import { SCOPE_KINDS } from '@pflegenavi/frontend/routing';
import type { ScopeKind } from '@pflegenavi/frontend/routing';
import type { LegalEntity, NursingHome, Tenant } from './types';
import { useStoredSelection } from './useStoredSelection';

const STORAGE_KEY = 'scope';

interface StoredScope {
  kind: ScopeKind;
}

interface ScopeContextProps extends StoredScope {
  nursingHomeList: NursingHome[];
  selectedNursingHome?: NursingHome;
  setNursingHomeList: (list: NursingHome[]) => Promise<void>;
  selectNursingHome: (nursingHome: NursingHome) => Promise<void>;

  legalEntityList: LegalEntity[];
  selectedLegalEntity?: LegalEntity;
  setLegalEntityList: (list: LegalEntity[]) => Promise<void>;
  selectLegalEntity: (legalEntity: LegalEntity) => Promise<void>;

  /**
   * Every legal entity the employee can see, for name lookups only. Scope
   * selection uses `legalEntityList`, which is filtered down to the entities
   * the employee is admin or manager in (ENG-2503) - a nursing home still needs
   * to show the name of the entity it belongs to either way.
   */
  knownLegalEntities: LegalEntity[];
  setKnownLegalEntities: (list: LegalEntity[]) => void;

  // There is only ever one tenant, so it has no list/selection pair.
  tenant?: Tenant;
  setTenant: (tenant: Tenant | undefined) => void;

  setScope: (scope: StoredScope) => void;

  /**
   * Whether the stored scope has been read back yet. It arrives asynchronously
   * and `kind` reads as the nursing home default until it does, so anything
   * that would act on the scope - a redirect, say - has to wait for this rather
   * than trust the first render.
   */
  isScopeRestored: boolean;
}

const DEFAULT_SCOPE: StoredScope = { kind: 'nursing_home' };

const isStoredScope = (value: unknown): value is StoredScope =>
  SCOPE_KINDS.includes((value as StoredScope)?.kind);

export const ScopeContext = createContext<ScopeContextProps | undefined>(
  undefined
);

const noop = (): void => undefined;
const asyncNoop = (): Promise<void> => Promise.resolve();

// No provider outside nursing-home-web, so the scope falls back to the
// nursing home those apps already assume.
export const FALLBACK_SCOPE: ScopeContextProps = {
  ...DEFAULT_SCOPE,
  nursingHomeList: [],
  legalEntityList: [],
  knownLegalEntities: [],
  setNursingHomeList: asyncNoop,
  selectNursingHome: asyncNoop,
  setLegalEntityList: asyncNoop,
  selectLegalEntity: asyncNoop,
  setKnownLegalEntities: noop,
  setTenant: noop,
  setScope: noop,
  // Nothing to restore without a provider, so the default is final.
  isScopeRestored: true,
};

export const useScope = (): ScopeContextProps =>
  useContext(ScopeContext) ?? FALLBACK_SCOPE;

export const useScopeFilter = (): {
  nursingHomeId?: string;
  legalEntityId?: string;
} => {
  const { kind, selectedNursingHome, selectedLegalEntity } = useScope();

  // eslint-disable-next-line consistent-return
  return useMemo(() => {
    switch (kind) {
      case 'nursing_home':
        return { nursingHomeId: selectedNursingHome?.id };
      case 'legal_entity':
        return { legalEntityId: selectedLegalEntity?.id };
      case 'tenant':
        return {};
    }
  }, [kind, selectedNursingHome?.id, selectedLegalEntity?.id]);
};

export const useIsScopeReady = (): boolean =>
  Object.values(useScopeFilter()).every(Boolean);

/**
 * The legal entity in force at the current scope: the selected one at legal
 * entity scope, the selected home's one at nursing home scope. Endpoints that
 * are keyed by legal entity at every scope (e.g. the Pliant transaction list)
 * need it resolved either way. `undefined` at tenant scope, which spans several
 * legal entities and so cannot name one.
 *
 * @example
 * const legalEntityId = useScopeLegalEntityId(); // '3f1c...'
 */
// eslint-disable-next-line consistent-return
export const useScopeLegalEntityId = (): string | undefined => {
  const { kind, selectedNursingHome, selectedLegalEntity } = useScope();

  switch (kind) {
    case 'nursing_home':
      return selectedNursingHome?.legalEntityId;
    case 'legal_entity':
      return selectedLegalEntity?.id;
    case 'tenant':
      return undefined;
  }
};

/**
 * Every legal entity the employee can see, for name lookups only. Lets a page
 * name a legal entity without reaching for the whole scope.
 *
 * @example
 * const legalEntities = useKnownLegalEntities(); // [{ id: '3f1c...', name: 'Caritas Wien' }]
 */
export const useKnownLegalEntities = (): LegalEntity[] =>
  useScope().knownLegalEntities;

/**
 * True while a single nursing home is selected, i.e. not at legal entity or
 * tenant scope. Use it to hide nursing-home-bound UI (quick actions, the
 * nursing home status menu, the employee mode selector) at the wider scopes,
 * where it would act on the last selected home instead of what is shown.
 */
export const useIsNursingHomeScope = (): boolean =>
  useScope().kind === 'nursing_home';

/**
 * The nursing homes the current scope covers, for pickers that narrow within it.
 * Kept here because branching on the scope kind is confined to this package - a
 * page would otherwise have to reach for the whole tenant list and re-derive the
 * narrowing itself.
 */
export const useScopedNursingHomes = (): NursingHome[] => {
  const { kind, nursingHomeList, selectedNursingHome, selectedLegalEntity } =
    useScope();

  // eslint-disable-next-line consistent-return
  return useMemo(() => {
    switch (kind) {
      case 'nursing_home':
        return selectedNursingHome ? [selectedNursingHome] : [];
      case 'legal_entity':
        return nursingHomeList.filter(
          (nursingHome) => nursingHome.legalEntityId === selectedLegalEntity?.id
        );
      case 'tenant':
        return nursingHomeList;
    }
  }, [kind, nursingHomeList, selectedNursingHome, selectedLegalEntity?.id]);
};

const SCOPE_PARAMS: Record<ScopeKind, string> = {
  nursing_home: 'nh_id',
  legal_entity: 'le_id',
  tenant: 'tenant',
};

/**
 * The query that pins a scope, e.g. `nh_id=3f1c...`, `le_id=9a2b...` or
 * `tenant=1`. A scope switch navigates to it, so it becomes a history entry
 * and Back restores the previous scope.
 *
 * @example
 * navigate({ search: scopeSearch('nursing_home', nursingHome.id) });
 */
export const scopeSearch = (kind: ScopeKind, id?: string): string =>
  new URLSearchParams({
    [SCOPE_PARAMS[kind]]: kind === 'tenant' ? '1' : id ?? '',
  }).toString();

// The scope is part of the URL while the app is open, so switching it is a
// navigation like any other. This keeps URL and state in step: a query without
// a scope param gets the current one (replace, keeping the rest of the query),
// and a query whose scope differs - Back, Forward, a deep link - is applied to
// the state. A query is applied once; when the state later moves away from it
// without a navigation (a scope the employee may not use, a stored home that is
// gone), the URL follows the state rather than pulling it back.
const ScopeUrlSync: FC<ScopeUrlSyncProps> = ({
  onUnavailableScopeInUrl,
  isNursingHomeListLoaded,
  isLegalEntityListLoaded,
}) => {
  const {
    kind,
    isScopeRestored,
    nursingHomeList,
    legalEntityList,
    selectedNursingHome,
    selectedLegalEntity,
    tenant,
    selectNursingHome,
    selectLegalEntity,
    setScope,
  } = useScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const handledSearch = useRef<string | undefined>(undefined);
  // The last query whose scope came from outside (a link, Back/Forward, a
  // switch) rather than being written here.
  const appliedSearch = useRef<string | undefined>(undefined);
  const reportedSearch = useRef<string | undefined>(undefined);

  const selectedId =
    kind === 'nursing_home'
      ? selectedNursingHome?.id
      : kind === 'legal_entity'
      ? selectedLegalEntity?.id
      : '';
  const selectedName =
    kind === 'nursing_home'
      ? selectedNursingHome?.name
      : kind === 'legal_entity'
      ? selectedLegalEntity?.name
      : tenant?.name;
  const isReady = isScopeRestored && selectedId !== undefined;

  useEffect(() => {
    const search = searchParams.toString();
    if (!isReady) {
      return;
    }

    // `nh_id` is more specific than `le_id`, so it wins - same as at mount.
    const urlKind = SCOPE_KINDS.find((candidate) =>
      searchParams.has(SCOPE_PARAMS[candidate])
    );
    const urlId = !urlKind
      ? undefined
      : urlKind === 'tenant'
      ? ''
      : searchParams.get(SCOPE_PARAMS[urlKind]);
    const isInSync = urlKind === kind && urlId === selectedId;

    // Writes the current scope into the query, replacing the history entry.
    const writeCurrentScope = () => {
      const next = new URLSearchParams(searchParams);
      SCOPE_KINDS.forEach((candidate) => next.delete(SCOPE_PARAMS[candidate]));
      next.set(SCOPE_PARAMS[kind], kind === 'tenant' ? '1' : selectedId);
      // Handled ahead of the write: the state may move on before the new
      // query arrives, and it must not be pulled back to what was written.
      handledSearch.current = next.toString();
      setSearchParams(next, { replace: true });
    };
    // The employee opened a link to a scope they cannot use, so tell them
    // they are looking at another one. Once per link, since the effect can run
    // again before the corrected query arrives.
    const reportUnavailable = () => {
      writeCurrentScope();
      if (reportedSearch.current !== search) {
        reportedSearch.current = search;
        onUnavailableScopeInUrl?.({ name: selectedName });
      }
    };

    if (handledSearch.current === search) {
      if (isInSync) {
        return;
      }
      // The state left the query's scope without a navigation. If that scope
      // came from outside, it was taken away because the employee may not use
      // it - e.g. the scope selector dropping a `tenant` link for someone who
      // is not a tenant admin.
      if (appliedSearch.current === search) {
        reportUnavailable();
      } else {
        writeCurrentScope();
      }
      return;
    }

    if (!urlKind) {
      writeCurrentScope();
      return;
    }

    if (isInSync) {
      handledSearch.current = search;
      appliedSearch.current = search;
      return;
    }

    // Apply the URL's scope. Not handled yet: the next run, once the state has
    // caught up, marks it. An id that is not in the list is left alone while
    // the list is still loading (the mount-time pinning may yet apply it); once
    // the list is there, the URL is wrong (edited by hand, or a scope the
    // employee may not select) and is put back to the current scope. The list
    // can be empty for good - most employees may not select any legal entity.
    if (urlKind === 'nursing_home') {
      const nursingHome = nursingHomeList.find((item) => item.id === urlId);
      if (!nursingHome) {
        if (isNursingHomeListLoaded) {
          reportUnavailable();
        }
        return;
      }
      selectNursingHome(nursingHome);
    } else if (urlKind === 'legal_entity') {
      const legalEntity = legalEntityList.find((item) => item.id === urlId);
      if (!legalEntity) {
        if (isLegalEntityListLoaded) {
          reportUnavailable();
        }
        return;
      }
      selectLegalEntity(legalEntity);
    }
    setScope({ kind: urlKind });
  }, [
    isReady,
    kind,
    selectedId,
    selectedName,
    onUnavailableScopeInUrl,
    searchParams,
    setSearchParams,
    nursingHomeList,
    legalEntityList,
    isNursingHomeListLoaded,
    isLegalEntityListLoaded,
    selectNursingHome,
    selectLegalEntity,
    setScope,
  ]);

  return null;
};

interface ScopeUrlSyncProps
  extends Pick<ScopeProviderProps, 'onUnavailableScopeInUrl'> {
  isNursingHomeListLoaded: boolean;
  isLegalEntityListLoaded: boolean;
}

interface ScopeProviderProps extends PropsWithChildren {
  /**
   * Optional function to get query parameters, so a link can pin the scope
   * (e.g. the welcome link's `nh_id`). If not provided, URL-based scope
   * selection is disabled.
   */
  getQueryParam?: (param: string) => string | null;
  /**
   * Called when the URL names a scope the employee cannot use (the nursing
   * home or legal entity does not exist, they have no access, or the scope is
   * not available to them), and the current scope is shown instead.
   *
   * @example
   * onUnavailableScopeInUrl={({ name }) => enqueueSnackbar(name)}
   */
  onUnavailableScopeInUrl?: (current: { name?: string }) => void;
}

export const ScopeProvider: FC<ScopeProviderProps> = ({
  children,
  getQueryParam,
  onUnavailableScopeInUrl,
}) => {
  const resources = useClientStoredResourcesContext();

  const [scope, setScopeInternal] = useState<StoredScope>(DEFAULT_SCOPE);
  const [isScopeRestored, setIsScopeRestored] = useState(false);
  const [tenant, setTenant] = useState<Tenant | undefined>(undefined);
  const [knownLegalEntities, setKnownLegalEntities] = useState<LegalEntity[]>(
    []
  );

  const applyScope = useCallback(
    (updater: (current: StoredScope) => StoredScope) => {
      setScopeInternal((current) => {
        const next = updater(current);
        if (next !== current) {
          resources.set(STORAGE_KEY, JSON.stringify(next));
        }
        return next;
      });
    },
    [resources]
  );

  // Read at mount only: ScopeUrlSync rewrites the URL afterwards, and pinning
  // to each rewrite flips the selection back and forth.
  const [{ urlNursingHomeId, urlLegalEntityId }] = useState(() => {
    const nursingHomeId = getQueryParam?.('nh_id') ?? null;
    return {
      urlNursingHomeId: nursingHomeId,
      // A `nh_id` link is more specific than a `le_id` one, so it wins.
      urlLegalEntityId: nursingHomeId ? null : getQueryParam?.('le_id') ?? null,
    };
  });

  const pinNursingHomeScope = useCallback(
    () =>
      applyScope((current) =>
        current.kind === 'nursing_home'
          ? current
          : { ...current, kind: 'nursing_home' }
      ),
    [applyScope]
  );

  const pinLegalEntityScope = useCallback(
    () =>
      applyScope((current) =>
        current.kind === 'legal_entity'
          ? current
          : { ...current, kind: 'legal_entity' }
      ),
    [applyScope]
  );

  const nursingHomes = useStoredSelection<NursingHome>({
    idKey: 'selectedNursingHomeId',
    objectKey: 'selectedNursingHome',
    urlId: urlNursingHomeId,
    onUrlIdApplied: pinNursingHomeScope,
  });

  const legalEntities = useStoredSelection<LegalEntity>({
    idKey: 'selectedLegalEntityId',
    objectKey: 'selectedLegalEntity',
    urlId: urlLegalEntityId,
    onUrlIdApplied: pinLegalEntityScope,
  });

  useEffect(() => {
    resources.registerResource(STORAGE_KEY);
  }, [resources]);

  useEffect(() => {
    (async function () {
      try {
        const stored = await resources.get(STORAGE_KEY);
        if (!stored) {
          return;
        }
        // A `nh_id`/`le_id`/`tenant` link pins the scope explicitly; the
        // selections above own it instead of the stored value.
        if (
          getQueryParam?.('nh_id') ||
          getQueryParam?.('le_id') ||
          getQueryParam?.('tenant')
        ) {
          return;
        }
        const parsed = JSON.parse(stored);
        setScopeInternal(
          isStoredScope(parsed) ? { kind: parsed.kind } : DEFAULT_SCOPE
        );
      } finally {
        // Every path above settles the scope, including the ones that keep the
        // default, so the flag belongs here rather than next to the setter.
        setIsScopeRestored(true);
      }
    })();
  }, [resources, getQueryParam]);

  useEffect(() => {
    // A `tenant` link has no id to wait for, so it applies at mount - unless
    // a more specific `nh_id`/`le_id` is also present, in which case the
    // selections above decide.
    if (!getQueryParam || getQueryParam('nh_id') || getQueryParam('le_id')) {
      return;
    }
    if (getQueryParam('tenant')) {
      applyScope((current) => ({ ...current, kind: 'tenant' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setScope = useCallback(
    (next: StoredScope) => applyScope(() => next),
    [applyScope]
  );

  const value = useMemo(
    () => ({
      ...scope,
      nursingHomeList: nursingHomes.list,
      selectedNursingHome: nursingHomes.selected,
      setNursingHomeList: nursingHomes.setList,
      selectNursingHome: nursingHomes.select,
      legalEntityList: legalEntities.list,
      selectedLegalEntity: legalEntities.selected,
      setLegalEntityList: legalEntities.setList,
      selectLegalEntity: legalEntities.select,
      knownLegalEntities,
      setKnownLegalEntities,
      tenant,
      setTenant,
      setScope,
      isScopeRestored,
    }),
    [
      scope,
      nursingHomes,
      legalEntities,
      knownLegalEntities,
      tenant,
      setScope,
      isScopeRestored,
    ]
  );

  // Tests and mobile render the provider without a router.
  const inRouter = useInRouterContext();

  return (
    <ScopeContext.Provider value={value}>
      {inRouter && (
        <ScopeUrlSync
          onUnavailableScopeInUrl={onUnavailableScopeInUrl}
          isNursingHomeListLoaded={nursingHomes.isListLoaded}
          isLegalEntityListLoaded={legalEntities.isListLoaded}
        />
      )}
      {children}
    </ScopeContext.Provider>
  );
};
